/*
 * FURVIBE — product page (sections/fv-product.liquid).
 * Variant picker (incl. "Colour / Size" split options + add-ons), one-image gallery with
 * arrows / swipe / drag / thumbnails / keyboard, zoom lightbox, add to bag through Shopify's
 * cart events (Horizon's drawer refreshes and opens), sticky bar, delivery estimate.
 */
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent } from '@shopify/events';

// US federal holidays (observed dates) for the delivery estimate. Keep in sync with assets/fv-help.js; extend before 2028.
const US_HOLIDAYS = new Set([
  '2026-10-12', '2026-11-11', '2026-11-26', '2026-12-25', '2027-01-01', '2027-01-18', '2027-02-15', '2027-05-31',
  '2027-06-18', '2027-07-05', '2027-09-06', '2027-10-11', '2027-11-11', '2027-11-25', '2027-12-24', '2027-12-31',
]);

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const ADD_URL = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || ROOT + 'cart/add.js';
const CART_URL = ((window.Theme && Theme.routes && Theme.routes.cart_url) || ROOT + 'cart') + '.js';
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
/* "6 in (15.5 cm)" -> "6 in": drop a trailing metric conversion. */
const shortValue = (v) => String(v || '').replace(/\s*\([^()]*\d\s?(?:cm|mm|m|ml|kg|g)\)\s*$/i, '');

class FvProduct extends HTMLElement {
  connectedCallback() {
    if (!this._ready) this.init();
    if (!this.data) return;
    this._ac = new AbortController();
    this._ios = [];
    this.bindWindow();
    this.observeSticky();
  }

  disconnectedCallback() {
    if (this._ac) this._ac.abort();
    (this._ios || []).forEach((io) => io.disconnect());
    this._ios = [];
  }

  init() {
    this._ready = true;
    const json = this.querySelector('[data-fv-json]');
    if (!json) return;
    this.data = JSON.parse(json.textContent);
    this.currency = this.dataset.currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    this.fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: this.currency });
    this.form = this.querySelector('[data-fv-form]');
    this.idField = this.querySelector('[data-fv-id]');
    this.variants = this.data.variants.filter((v) => v.o && v.o.length);
    this.variant = this.data.variants.find((v) => String(v.id) === String(this.idField && this.idField.value)) || this.variants[0];
    // An add-on (e.g. ?variant=<spatula>) has no picker values: anchor the pickers on a real variant.
    const anchor = this.variant && this.variant.o && this.variant.o.length ? this.variant : this.variants[0];
    this.selected = anchor ? [...anchor.o] : [];

    this.initOptions();
    this.initGallery();
    this.initBuy();
    this.initSticky();
    this.initEta();
    this.refreshStates();
  }

  money(cents) {
    return this.fmt.format((cents || 0) / 100);
  }

  /* ------------------------------------------------------------ options */
  initOptions() {
    const opts = this.querySelector('[data-fv-opts]');
    if (!opts) return;
    opts.addEventListener('change', (e) => {
      const dimEl = e.target.closest('[data-fv-dim]');
      if (!dimEl) return;
      this.choose(Number(dimEl.dataset.fvDim), e.target.value);
    });
    // Hovering a swatch previews its name next to the label.
    opts.addEventListener('pointerover', (e) => {
      const sw = e.target.closest('.fv-v--swatch');
      if (!sw) return;
      const dimEl = sw.closest('[data-fv-dim]');
      const out = dimEl.querySelector('[data-fv-dim-value]');
      if (out) out.textContent = shortValue(sw.dataset.fvValue);
      // Warm up that colour's photo so the switch is instant.
      const d = Number(dimEl.dataset.fvDim);
      const v = this.variants.find((x) => x.o[d] === sw.dataset.fvValue && x.media);
      const img = v && this.track && this.track.querySelector(`[data-media="${v.media}"] img[loading="lazy"]`);
      if (img) img.loading = 'eager';
    });
    opts.addEventListener('pointerout', (e) => {
      const sw = e.target.closest('.fv-v--swatch');
      if (!sw || sw.contains(e.relatedTarget)) return;
      const dimEl = sw.closest('[data-fv-dim]');
      const out = dimEl.querySelector('[data-fv-dim-value]');
      if (out && this.variant) out.textContent = shortValue(this.variant.o[Number(dimEl.dataset.fvDim)]);
    });
    this.querySelectorAll('[data-fv-guide]').forEach((btn) =>
      btn.addEventListener('click', () => {
        const target = document.getElementById('fv-sizing');
        if (target) {
          target.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
          return;
        }
        const acc = this.querySelector('[data-fv-acc="sizing"], [data-fv-acc="size-guide"]');
        if (!acc) return;
        acc.open = true;
        acc.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
        acc.querySelector('summary') && acc.querySelector('summary').focus({ preventScroll: true });
      })
    );
  }

  choose(dim, value) {
    const sel = [...this.selected];
    sel[dim] = value;
    const exact = this.variants.find((v) => v.o.every((o, i) => o === sel[i]));
    let next = exact && exact.available ? exact : null;
    if (!next) {
      // Keep the clicked value; keep as many other choices as possible; prefer available.
      const withValue = this.variants.filter((v) => v.o[dim] === value);
      const score = (v) => v.o.reduce((s, o, i) => s + (i !== dim && o === sel[i] ? 1 : 0), 0);
      const avail = withValue.filter((v) => v.available).sort((a, b) => score(b) - score(a));
      next = avail[0] || exact || withValue.sort((a, b) => score(b) - score(a))[0];
    }
    if (next) this.setVariant(next);
  }

  setVariant(v, silent) {
    this.variant = v;
    const err = this.querySelector('[data-fv-error]');
    if (err) err.hidden = true;
    this.selected = [...v.o];
    if (this.idField) {
      this.idField.value = String(v.id);
      this.idField.dispatchEvent(new Event('change', { bubbles: true }));
    }
    // Pickers
    this.querySelectorAll('[data-fv-dim]').forEach((dimEl) => {
      const d = Number(dimEl.dataset.fvDim);
      const val = v.o[d];
      const out = dimEl.querySelector('[data-fv-dim-value]');
      if (out) out.textContent = shortValue(val);
      dimEl.querySelectorAll('input[type="radio"]').forEach((r) => (r.checked = r.value === val));
      const s = dimEl.querySelector('[data-fv-dim-select]');
      if (s) s.value = val;
    });
    // Price + buttons
    this.querySelectorAll('[data-fv-price]').forEach((el) => (el.textContent = this.money(v.price)));
    const cmp = this.querySelector('[data-fv-compare]');
    if (cmp) {
      cmp.hidden = !(v.compare > v.price);
      cmp.textContent = this.money(v.compare);
    }
    this.syncButtons();
    const sv = this.querySelector('[data-fv-sticky-variant]');
    if (sv) sv.textContent = v.title;
    // URL
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (e) { /* ignore */ }
    const express = this.querySelector('[data-fv-express]');
    if (express) express.hidden = !v.available;
    this.syncGallery(v);
    this.refreshStates();
    if (!silent) this.emitSelect(v);
  }

  emitSelect(v) {
    try {
      const names = this.data.optionNames || [];
      const selectedOptions = names.map((name, i) => ({ name, value: (v.options || [])[i] })).filter((o) => o.value != null);
      if (!selectedOptions.length) return;
      const d = ProductSelectEvent.createPromise();
      this.dispatchEvent(new ProductSelectEvent({
        product: { id: String(this.data.id), title: this.data.title, handle: this.data.handle },
        selectedOptions,
        detail: { variantId: String(v.id), source: 'fv-product' },
        promise: d.promise,
      }));
      d.resolve({
        variant: {
          id: String(v.id), title: v.title, availableForSale: v.available, selectedOptions,
          price: { amount: (v.price / 100).toFixed(2), currencyCode: this.currency },
        },
        detail: { source: 'fv-product', resource: v },
      });
    } catch (e) { /* analytics only */ }
  }

  /* Free-shipping nudge: what's already in the bag + this line. */
  syncFreeShip() {
    const box = this.querySelector('[data-fv-fs]');
    if (!box) return;
    const threshold = Number(box.dataset.threshold) || 0;
    if (!threshold) return;
    const basis = (Number(box.dataset.cart) || 0) + this.lineTotal();
    const left = threshold - basis;
    const pct = Math.max(0, Math.min(100, Math.round((basis / threshold) * 100)));
    box.style.setProperty('--fv-fs', pct + '%');
    box.classList.toggle('is-done', left <= 0);
    const msg = box.querySelector('[data-fv-fs-msg]');
    if (msg) msg.innerHTML = left > 0
      ? `Add <strong>${this.money(left)}</strong> more for free US shipping`
      : '<strong>Free US shipping</strong> unlocked with this order';
  }

  syncButtons() {
    this.syncFreeShip();
    const v = this.variant;
    const ok = !!(v && v.available);
    this.querySelectorAll('[data-fv-atc], [data-fv-sticky-atc]').forEach((btn) => {
      btn.disabled = !ok;
      const label = btn.querySelector('[data-fv-atc-label]');
      if (label && !btn.classList.contains('is-done')) label.textContent = ok ? 'Add to bag' : 'Sold out';
      const price = btn.querySelector('[data-fv-atc-price]');
      if (price && v) price.textContent = this.money(this.lineTotal());
    });
  }

  lineTotal() {
    const q = Math.max(1, parseInt((this.qtyInput && this.qtyInput.value) || '1', 10) || 1);
    let total = (this.variant ? this.variant.price : 0) * q;
    this.querySelectorAll('[data-fv-addon]:checked').forEach((cb) => {
      const a = this.data.variants.find((x) => String(x.id) === cb.value);
      if (a) total += a.price;
    });
    return total;
  }

  refreshStates() {
    const sel = this.selected;
    this.querySelectorAll('[data-fv-dim]').forEach((dimEl) => {
      const d = Number(dimEl.dataset.fvDim);
      const matchesFor = (value) => this.variants.filter((v) => v.o[d] === value && v.o.every((o, i) => i === d || o === sel[i]));
      const state = (value) => {
        const matches = matchesFor(value);
        if (!matches.length) return 'missing';
        return matches.some((v) => v.available) ? 'ok' : 'soldout';
      };
      // "+$8" on values that cost more than the cheapest choice (given the other selections).
      const priceOf = (value) => {
        const m = matchesFor(value);
        return m.length ? Math.min(...m.map((v) => v.price)) : null;
      };
      const values = [...dimEl.querySelectorAll('[data-fv-value]')].map((el) => el.dataset.fvValue);
      const selectEl = dimEl.querySelector('[data-fv-dim-select]');
      const allValues = values.length ? values : selectEl ? [...selectEl.options].map((o) => o.value) : [];
      const prices = allValues.map(priceOf).filter((x) => x != null);
      const floor = prices.length ? Math.min(...prices) : 0;
      const delta = (value) => {
        const pr = priceOf(value);
        return pr != null && pr > floor ? '+' + this.money(pr - floor).replace(/\.00$/, '') : '';
      };
      dimEl.querySelectorAll('[data-fv-delta]').forEach((el) => {
        const host = el.closest('[data-fv-value]');
        el.textContent = host ? delta(host.dataset.fvValue) : '';
      });
      dimEl.querySelectorAll('[data-fv-value]').forEach((el) => {
        const s = state(el.dataset.fvValue);
        el.classList.toggle('is-soldout', s === 'soldout');
        el.classList.toggle('is-missing', s === 'missing');
        const note = s === 'soldout' ? ' — sold out' : s === 'missing' ? ' — unavailable in this combination' : '';
        el.title = el.dataset.fvValue + note;
        const t = el.querySelector('.fv-sr');
        if (t) t.textContent = el.classList.contains('fv-v--swatch') ? el.title : note;
      });
      const select = dimEl.querySelector('[data-fv-dim-select]');
      if (select) {
        [...select.options].forEach((o) => {
          const s = state(o.value);
          const dl = delta(o.value);
          o.textContent = shortValue(o.value) + (dl ? `  ${dl}` : '') + (s === 'soldout' ? ' — sold out' : s === 'missing' ? ' — n/a' : '');
        });
      }
    });
  }

  /* ------------------------------------------------------------ gallery */
  initGallery() {
    this.gallery = this.querySelector('[data-fv-gallery]');
    this.track = this.querySelector('[data-fv-track]');
    if (!this.track) return;
    this.thumbs = this.querySelector('[data-fv-thumbs]');
    this.index = 0;
    this._goTarget = null;

    let raf = 0;
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => this.onScroll());
    }, { passive: true });
    this.track.addEventListener('scrollend', () => {
      this._goTarget = null;
      this.onScroll();
    });
    // The shopper's own swipe / wheel takes over from a programmatic glide.
    ['pointerdown', 'touchstart', 'wheel'].forEach((t) =>
      this.track.addEventListener(t, () => (this._goTarget = null), { passive: true })
    );

    this.querySelector('[data-fv-prev]')?.addEventListener('click', () => this.go(this.target() - 1));
    this.querySelector('[data-fv-next]')?.addEventListener('click', () => this.go(this.target() + 1));
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.target() + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.target() - 1); }
    });
    this.thumbs?.addEventListener('click', (e) => {
      const li = e.target.closest('li[data-media]');
      if (!li) return;
      const i = this.visibleSlides().findIndex((s) => s.dataset.media === li.dataset.media);
      if (i > -1) this.go(i);
    });

    // Mouse drag (touch & trackpads already swipe natively through scroll-snap); the window
    // listeners live in bindWindow() so they can be removed when the section is re-rendered.
    this._drag = null;
    this.track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      this._drag = { x: e.clientX, left: this.track.scrollLeft, moved: false };
    });

    this.track.addEventListener('click', (e) => {
      const z = e.target.closest('[data-fv-zoom]');
      if (!z || this._suppressClick) return;
      this.openLightbox(this.index);
    });
    this.updateGalleryUi();
  }

  /* Window-level listeners and observers: re-created on every connect, removed on disconnect. */
  bindWindow() {
    const signal = this._ac.signal;
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted || !this.idField) return;
      const v = this.data.variants.find((x) => String(x.id) === this.idField.value);
      if (v && v !== this.variant && v.o && v.o.length) this.setVariant(v, true);
    }, { signal });
    if (!this.track) return;
    window.addEventListener('pointermove', (e) => {
      const drag = this._drag;
      if (!drag) return;
      const dx = e.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) > 6) {
        drag.moved = true;
        this._goTarget = null;
        this.track.classList.add('is-dragging');
      }
      if (drag.moved) this.track.scrollLeft = drag.left - dx;
    }, { signal });
    window.addEventListener('pointerup', (e) => {
      const drag = this._drag;
      if (!drag) return;
      this._drag = null;
      if (!drag.moved) return;
      const dx = e.clientX - drag.x;
      this.track.classList.remove('is-dragging');
      this._suppressClick = true;
      setTimeout(() => (this._suppressClick = false), 60);
      const w = this.track.clientWidth || 1;
      const base = Math.round(drag.left / w);
      const target = Math.abs(dx) > w * 0.12 ? base + (dx < 0 ? 1 : -1) : base;
      this.go(Math.max(0, Math.min(target, this.visibleSlides().length - 1)), false);
    }, { signal });
    window.addEventListener('resize', () => this.go(this.index, false, true), { passive: true, signal });
  }

  visibleSlides() {
    return this.track ? [...this.track.children].filter((s) => !s.hidden) : [];
  }

  /* Where the gallery is heading: the pending glide target, else the settled slide. */
  target() {
    return this._goTarget != null ? this._goTarget : this.index;
  }

  onScroll() {
    const w = this.track.clientWidth || 1;
    const pos = this.track.scrollLeft / w;
    if (this._goTarget != null) {
      if (Math.abs(pos - this._goTarget) > 0.02) return; // our own smooth scroll is still travelling
      this._goTarget = null;
    }
    const i = Math.round(pos);
    if (i !== this.index) {
      this.index = i;
      this.updateGalleryUi();
    }
  }

  go(i, wrap = true, instant = false) {
    const n = this.visibleSlides().length;
    if (!n || !this.track) return;
    if (wrap) i = (i + n) % n;
    else i = Math.max(0, Math.min(i, n - 1));
    this.index = i;
    const behavior = instant || reduceMotion() ? 'auto' : 'smooth';
    this._goTarget = behavior === 'smooth' ? i : null;
    this.track.scrollTo({ left: i * this.track.clientWidth, behavior });
    this.updateGalleryUi();
  }

  updateGalleryUi() {
    const slides = this.visibleSlides();
    const n = slides.length;
    const count = this.querySelector('[data-fv-count]');
    const total = this.querySelector('[data-fv-total]');
    if (count) count.textContent = String(Math.min(this.index + 1, n));
    if (total) total.textContent = String(n);
    this.gallery && this.gallery.classList.toggle('fv-g--single', n < 2);
    const active = slides[this.index];
    // Keep keyboard focus inside the gallery when the slide holding it becomes inert.
    const f = document.activeElement;
    const refocus = f && f !== this.track && this.track.contains(f) && !(active && active.contains(f));
    slides.forEach((s, k) => {
      s.setAttribute('aria-label', `${k + 1} of ${n}`);
      s.toggleAttribute('inert', k !== this.index);
    });
    if (refocus) this.track.focus({ preventScroll: true });
    if (this.thumbs && active) {
      const behavior = reduceMotion() ? 'auto' : 'smooth';
      this.thumbs.querySelectorAll('li[data-media]').forEach((li) => {
        const on = li.dataset.media === active.dataset.media;
        const b = li.querySelector('button');
        if (b) on ? b.setAttribute('aria-current', 'true') : b.removeAttribute('aria-current');
        if (on) {
          const box = this.thumbs;
          const vertical = box.scrollHeight > box.clientHeight + 2;
          if (vertical) box.scrollTo({ top: li.offsetTop - box.clientHeight / 2 + li.clientHeight / 2, behavior });
          else box.scrollTo({ left: li.offsetLeft - box.clientWidth / 2 + li.clientWidth / 2, behavior });
        }
      });
    }
  }

  syncGallery(v) {
    if (!this.track) return;
    const media = v.media ? String(v.media) : null;
    // Photos with supplier text are only shown for the colour they belong to.
    const reveal = (list) => {
      if (!list) return;
      [...list.children].forEach((el) => {
        if (!el.hasAttribute('data-text')) return;
        const ids = (el.dataset.variants || '').split(',').filter(Boolean);
        if (ids.length) el.hidden = !(media && el.dataset.media === media);
      });
    };
    reveal(this.track);
    reveal(this.thumbs);
    if (!media) {
      this.updateGalleryUi();
      return;
    }
    const target = this.track.querySelector(`:scope > [data-media="${media}"]`);
    const img = target && target.querySelector('img[loading="lazy"]');
    if (img) img.loading = 'eager';
    const i = target ? this.visibleSlides().indexOf(target) : -1;
    if (i > -1) this.go(i, false);
    else this.go(Math.min(this.index, this.visibleSlides().length - 1), false, true);
  }

  /* ----------------------------------------------------------- lightbox */
  openLightbox(i) {
    const dlg = this.querySelector('[data-fv-lb]');
    const track = this.querySelector('[data-fv-lb-track]');
    if (!dlg || !track || typeof dlg.showModal !== 'function') return;
    track.innerHTML = '';
    this.visibleSlides().forEach((s) => {
      const img = s.querySelector('img');
      if (!img) return;
      const li = document.createElement('li');
      li.className = 'fv-lb__slide';
      const big = img.cloneNode();
      big.removeAttribute('class');
      big.removeAttribute('fetchpriority');
      big.setAttribute('sizes', '100vw');
      big.loading = 'eager';
      li.appendChild(big);
      track.appendChild(li);
    });
    this._lbN = track.children.length;
    const total = this.querySelector('[data-fv-lb-total]');
    if (total) total.textContent = String(this._lbN);
    const count = this.querySelector('[data-fv-lb-count]');
    const set = (k, instant) => {
      const n = this._lbN || 1;
      this._lbI = (k + n) % n;
      track.scrollTo({ left: this._lbI * track.clientWidth, behavior: instant || reduceMotion() ? 'auto' : 'smooth' });
      if (count) count.textContent = String(this._lbI + 1);
    };
    this._lbSet = set;
    if (!this._lbBound) {
      this._lbBound = true;
      this.querySelector('[data-fv-lb-close]')?.addEventListener('click', () => dlg.close());
      this.querySelector('[data-fv-lb-prev]')?.addEventListener('click', () => this._lbSet(this._lbI - 1));
      this.querySelector('[data-fv-lb-next]')?.addEventListener('click', () => this._lbSet(this._lbI + 1));
      // Clicking the empty space around the picture closes it.
      dlg.addEventListener('click', (e) => {
        const t = e.target;
        if (t === dlg || t === track || (t instanceof Element && t.classList.contains('fv-lb__slide'))) dlg.close();
      });
      dlg.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this._lbSet(this._lbI + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this._lbSet(this._lbI - 1); }
      });
      // Swipes inside the lightbox: settle the index once the scroll lands.
      track.addEventListener('scrollend', () => {
        this._lbI = Math.round(track.scrollLeft / (track.clientWidth || 1));
        if (count) count.textContent = String(this._lbI + 1);
      });
      dlg.addEventListener('close', () => {
        document.documentElement.classList.remove('fv-lb-open');
        if (this._lbI !== this.index) this.go(this._lbI, false, true);
      });
    }
    dlg.showModal();
    document.documentElement.classList.add('fv-lb-open');
    requestAnimationFrame(() => set(i, true));
  }

  /* ---------------------------------------------------------------- buy */
  initBuy() {
    this.qtyInput = this.querySelector('[data-fv-qty-input]');
    this.querySelectorAll('[data-fv-qty]').forEach((b) =>
      b.addEventListener('click', () => {
        const q = Math.max(1, Math.min(99, (parseInt(this.qtyInput.value, 10) || 1) + Number(b.dataset.fvQty)));
        this.qtyInput.value = String(q);
        this.syncButtons();
      })
    );
    this.qtyInput?.addEventListener('change', () => {
      this.qtyInput.value = String(Math.max(1, Math.min(99, parseInt(this.qtyInput.value, 10) || 1)));
      this.syncButtons();
    });
    this.querySelectorAll('[data-fv-addon]').forEach((cb) => cb.addEventListener('change', () => this.syncButtons()));
    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.addToCart();
    });
    this.syncButtons();
  }

  async addToCart() {
    const v = this.variant;
    if (!v || !v.available || this._busy) return;
    this._busy = true;
    const btns = [...this.querySelectorAll('[data-fv-atc], [data-fv-sticky-atc]')];
    btns.forEach((b) => b.classList.add('is-busy'));
    const err = this.querySelector('[data-fv-error]');
    if (err) err.hidden = true;

    const qty = Math.max(1, parseInt((this.qtyInput && this.qtyInput.value) || '1', 10) || 1);
    const items = [{ id: Number(v.id), quantity: qty }];
    // Keep line-item properties that apps inject into the product form.
    if (this.form) {
      const props = {};
      for (const [k, val] of new FormData(this.form)) {
        const m = /^properties\[(.+)\]$/.exec(k);
        if (m && typeof val === 'string' && val !== '') props[m[1]] = val;
      }
      if (Object.keys(props).length) items[0].properties = props;
    }
    this.querySelectorAll('[data-fv-addon]:checked').forEach((cb) => items.push({ id: Number(cb.value), quantity: 1 }));
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId).filter(Boolean);

    const fail = (msg) => {
      if (err) {
        err.textContent = msg;
        err.hidden = false;
      }
      btns.forEach((b) => b.classList.remove('is-busy'));
      this._busy = false;
    };

    let res;
    let json;
    try {
      res = await fetch(ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      json = await res.json();
    } catch (e) {
      this.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
      return fail('Connection problem. Please try again.');
    }
    if (!res.ok || json.status) {
      const msg = json.description || json.message || 'Could not add to bag. Please try again.';
      this.dispatchEvent(new CartErrorEvent({ error: msg, code: 'INVALID', detail: { description: json.description, errors: json.errors } }));
      return fail(msg);
    }

    // Success: let Horizon's drawer morph + open, and the header count update.
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((it) => ({ merchandiseId: String(it.id), quantity: it.quantity })),
      promise: deferred.promise,
    }));
    const itemCount = items.reduce((n, it) => n + it.quantity, 0);
    try {
      const cart = await fetch(CART_URL, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: json.sections, source: 'fv-product', itemCount, didError: false },
      });
      const fs = this.querySelector('[data-fv-fs]');
      if (fs && typeof cart.total_price === 'number') fs.dataset.cart = String(cart.total_price);
    } catch (e) {
      deferred.resolve({ cart: null, detail: { sections: json.sections, source: 'fv-product', didError: false } });
    }

    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer && !drawer.querySelector('cart-drawer-component[auto-open]') && typeof drawer.open === 'function') {
      setTimeout(() => drawer.open(), 150);
    } else if (!drawer && !document.querySelector('cart-drawer-component')) {
      window.location.href = (window.Theme && Theme.routes && Theme.routes.cart_url) || ROOT + 'cart';
    }

    btns.forEach((b) => {
      b.classList.remove('is-busy');
      b.classList.add('is-done');
      const l = b.querySelector('[data-fv-atc-label]');
      if (l) l.textContent = 'Added';
    });
    setTimeout(() => {
      btns.forEach((b) => b.classList.remove('is-done'));
      this.syncButtons();
    }, 1800);
    this.querySelectorAll('[data-fv-addon]:checked').forEach((cb) => (cb.checked = false));
    this.syncButtons();
    this._busy = false;
  }

  /* ------------------------------------------------------------- sticky */
  initSticky() {
    const bar = this.querySelector('[data-fv-sticky]');
    if (!bar) return;
    bar.querySelector('[data-fv-sticky-atc]')?.addEventListener('click', () => {
      if (this.form && typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
      else this.addToCart();
    });
  }

  /* Visible once the main button has scrolled away upwards; tucked away again over the footer. */
  observeSticky() {
    const bar = this.querySelector('[data-fv-sticky]');
    const anchor = this.querySelector('.fv-bb__buy');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    let past = false;
    let atFooter = false;
    const sync = () => {
      const on = past && !atFooter;
      bar.classList.toggle('is-on', on);
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      bar.toggleAttribute('inert', !on);
    };
    const io1 = new IntersectionObserver(([entry]) => {
      past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sync();
    });
    io1.observe(anchor);
    this._ios.push(io1);
    const footer = document.querySelector('footer');
    if (footer) {
      const io2 = new IntersectionObserver(([entry]) => {
        atFooter = entry.isIntersecting;
        sync();
      }, { rootMargin: '0px 0px -80px 0px' });
      io2.observe(footer);
      this._ios.push(io2);
    }
  }

  /* ---------------------------------------------------------------- eta */
  initEta() {
    const li = this.querySelector('[data-fv-eta]');
    const out = this.querySelector('[data-fv-eta-text]');
    if (!li || !out) return;
    const p = (li.dataset.processing || '').match(/\d+/g);
    const t = (li.dataset.transit || '').match(/\d+/g);
    if (!p || !t) return;
    const min = Number(p[0]) + Number(t[0]);
    const max = Number(p[p.length - 1]) + Number(t[t.length - 1]);
    // Business days skip weekends and US federal holidays (Shipping Policy), counted from today in New York.
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
    const get = (t) => Number(parts.find((x) => x.type === t)?.value);
    const add = (n) => {
      const d = new Date(Date.UTC(get('year'), get('month') - 1, get('day')));
      let left = n;
      while (left > 0) {
        d.setUTCDate(d.getUTCDate() + 1);
        const wd = d.getUTCDay();
        if (wd !== 0 && wd !== 6 && !US_HOLIDAYS.has(d.toISOString().slice(0, 10))) left -= 1;
      }
      return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    };
    const a = add(min);
    const b = add(max);
    const m = (d) => d.toLocaleDateString('en-US', { month: 'short' });
    const range = m(a) === m(b) ? `${m(a)} ${a.getDate()}–${b.getDate()}` : `${m(a)} ${a.getDate()} – ${m(b)} ${b.getDate()}`;
    const strong = out.querySelector('strong');
    const free = strong ? strong.outerHTML : '';
    out.innerHTML = `Estimated delivery <b class="fv-bb__eta">${range}</b>${free ? ' · ' + free : ''}`;
  }
}

if (!customElements.get('fv-product')) customElements.define('fv-product', FvProduct);
